/**
 * Storage composable for Firebase Storage operations
 * Wraps the storage service with reactive state
 */
import { ref } from 'vue'
import { uploadFile, uploadFileWithProgress, deleteFile } from '@/core/firebase/storage'
import { getFirebaseStorage } from '@/core/firebase/firebase'
import { ref as storageRef, deleteObject, getDownloadURL } from 'firebase/storage'

/**
 * Composable for file storage operations
 * @returns {Object} Storage methods and reactive state
 */
export function useStorage() {
  const error = ref(null)
  const url = ref(null)
  const uploading = ref(false)
  const progress = ref(0)

  /**
   * Upload an image file
   * @param {File|Blob} file - File to upload
   * @param {string} folder - Storage folder path
   * @param {string} name - Optional custom name for the file
   * @returns {Promise<string>} Download URL
   */
  async function uploadImage(file, folder, name = null) {
    error.value = null
    uploading.value = true
    progress.value = 0

    try {
      const fileName = name ? `${name}_${Date.now()}` : `${Date.now()}_${file.name || 'image'}`
      const path = `${folder}/${fileName}`

      url.value = await uploadFileWithProgress(path, file, (p) => {
        progress.value = p
      })

      return url.value
    } catch (err) {
      console.error('Upload error:', err)
      error.value = err.message
      throw err
    } finally {
      uploading.value = false
    }
  }

  /**
   * Delete an image by its download URL
   * @param {string} imageUrl - The download URL of the image to delete
   */
  async function deleteImageByUrl(imageUrl) {
    error.value = null

    try {
      const storage = getFirebaseStorage()
      const imageRef = storageRef(storage, getPathFromUrl(imageUrl))
      await deleteObject(imageRef)
    } catch (err) {
      // If file doesn't exist, treat as success (already deleted)
      if (err.code === 'storage/object-not-found') {
        return
      }
      console.error('Delete error:', err)
      error.value = err.message
      throw err
    }
  }

  /**
   * Delete an image by its storage path
   * @param {string} path - Storage path
   */
  async function deleteImage(path) {
    error.value = null

    try {
      await deleteFile(path)
    } catch (err) {
      console.error('Delete error:', err)
      error.value = err.message
      throw err
    }
  }

  /**
   * Extract storage path from a Firebase Storage download URL
   * @param {string} url - Download URL
   * @returns {string} Storage path
   */
  function getPathFromUrl(downloadUrl) {
    if (!downloadUrl) return ''

    try {
      // Firebase Storage URLs contain the path after '/o/' and before '?'
      const match = downloadUrl.match(/\/o\/(.+?)\?/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
    } catch (e) {
      console.error('Error parsing URL:', e)
    }

    return ''
  }

  return {
    error,
    url,
    uploading,
    progress,
    uploadImage,
    deleteImage,
    deleteImageByUrl,
  }
}
